import { AlertTriangleIcon, CoinsIcon, PlusIcon, ReceiptIcon, RefreshCwIcon, UndoIcon } from 'lucide-react'
import { useRef, useState, useEffect, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import { ApiError, errorMessage } from '@/api/client'
import {
  useBalance,
  useBalanceTransactions,
  useMe,
  usePoliceRecharge,
  useRefundable,
  useRequestRefund,
  useSavePoliceRecharge,
  useTopUpCheckout,
} from '@/api/queries'
import type { BalanceTransactionRow, CheckoutSession, PoliceRecharge, Refundable } from '@/api/types'
import { EmbeddedCheckoutDialog } from '@/components/billing/EmbeddedCheckoutDialog'
import { PdfButton } from '@/components/billing/PdfButton'
import { hasBilling } from '@/lib/access'
import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  checkRefundAmount,
  checkUsdAmount,
  formatUsd,
  ledgerDocument,
  refundRequestLabel,
  refundRequestOpen,
  stripeReturn,
} from '@/lib/billing'
import { formatAbsolute, formatBps, formatMicros } from '@/lib/format'
import { useReadOnly } from '@/staff/hooks'

/** How long a top-up return keeps polling for the webhook's credit. */
const CREDIT_WAIT_MS = 60_000

/**
 * The POLICE BALANCE page (`/app/billing`; "Police balance" in the sidebar): prepaid money for
 * managed police, top-ups, auto-recharge, and the ledger (decision D27, stripe.md §8).
 *
 * <h2>Not the Plan page</h2>
 * The subscription (Free / Solo / Team) lives on `/app/plan` (`PlanPage`). This page is money that
 * managed police draws down; the two share a Stripe customer and nothing else.
 *
 * <h2>The page never credits anything</h2>
 * A top-up is Stripe Checkout. Coming back (`?topup=success`) only polls the balance; the credit is
 * written by the webhook, once, server-side.
 *
 * <h2>Why the ledger shows the whole basis</h2>
 * Every charge lists its four token counters and the markup, because **an unexplainable charge is a
 * chargeback**. A customer who cannot see how a number was reached has only one way to dispute it.
 *
 * <h2>Two things this page must say out loud</h2>
 * That **failed calls are still charged** — the provider bills us for tokens a failed call consumed,
 * so we bill for them — and that **auto-recharge is off until it is enabled with a saved card**, so
 * nobody leaves believing their balance will top itself up.
 */
const TOPUP_BASELINE_KEY = 'saltseed.topup.baseline'

function saveTopUpBaseline(micros: number) {
  try {
    window.sessionStorage.setItem(TOPUP_BASELINE_KEY, String(micros))
  } catch {
    // Storage unavailable: the page falls back to the first balance it sees on return.
  }
}

function readTopUpBaseline(): number | null {
  try {
    const raw = window.sessionStorage.getItem(TOPUP_BASELINE_KEY)
    window.sessionStorage.removeItem(TOPUP_BASELINE_KEY)
    const value = raw === null ? Number.NaN : Number(raw)
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

export function BillingPage() {
  const me = useMe()
  const location = useLocation()
  const returned = stripeReturn(location.search)
  const awaitingCredit = returned?.kind === 'topup' && returned.outcome === 'success'
  const [pollStopped, setPollStopped] = useState(false)
  const pollMs = awaitingCredit && !pollStopped ? 2_000 : false
  const balance = useBalance(pollMs)
  const transactions = useBalanceTransactions(pollMs)
  const topUp = useTopUpCheckout()
  const readOnly = useReadOnly()
  const [amount, setAmount] = useState('25')
  const [saveCard, setSaveCard] = useState(false)
  const navigate = useNavigate()
  const [checkout, setCheckout] = useState<CheckoutSession | null>(null)
  const firstBalance = useRef<number | null>(null)

  useEffect(() => {
    if (!awaitingCredit) return
    const id = window.setTimeout(() => {
      setPollStopped(true)
    }, CREDIT_WAIT_MS)
    return () => {
      window.clearTimeout(id)
    }
  }, [awaitingCredit])

  const isAdmin = hasBilling({ role: me.data?.role, billing: me.data?.billing })
  const minimumUsd = (balance.data?.minimum_topup_micros ?? 25_000_000) / 1_000_000
  const currency = balance.data?.currency ?? 'USD'
  const check = checkUsdAmount(amount, minimumUsd)

  // The baseline is the balance BEFORE Checkout, saved when the admin left for Stripe: the webhook often
  // lands before the browser comes back, so the first balance seen on return may already include it.
  if (balance.data !== undefined && firstBalance.current === null) {
    firstBalance.current = readTopUpBaseline() ?? balance.data.balance_micros
  }
  const credited =
    awaitingCredit &&
    balance.data !== undefined &&
    firstBalance.current !== null &&
    balance.data.balance_micros > firstBalance.current

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!check.ok) return
    topUp.mutate(
      { amountUsd: check.value, saveCard },
      {
        onSuccess: (result) => {
          if (balance.data !== undefined) saveTopUpBaseline(balance.data.balance_micros)
          setCheckout(result)
        },
      },
    )
  }

  if (!isAdmin) {
    return (
      <>
        <PageHeader
          title="Police balance"
          description="Your account's prepaid balance and the ledger of what drew on it"
        />
        <PageBody>
          <Alert>
            <AlertTitle>Billing access only</AlertTitle>
            <AlertDescription>
              The balance and its transactions are visible to account administrators and members with billing access.
            </AlertDescription>
          </Alert>
        </PageBody>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Police balance"
        description={
          <>
            One prepaid balance for the whole account, and a ledger showing what drew on it. Managed
            police is what meters against it today. Your subscription is on the{' '}
            <Link to="/app/plan" className="text-primary underline-offset-4 hover:underline">
              Plan
            </Link>{' '}
            page.
          </>
        }
      />
      <PageBody>
        {returned?.kind === 'topup' && returned.outcome === 'cancelled' ? (
          <Alert data-testid="topup-cancelled">
            <AlertTitle>Top-up cancelled</AlertTitle>
            <AlertDescription>Nothing was charged.</AlertDescription>
          </Alert>
        ) : null}
        {awaitingCredit ? (
          credited ? (
            <Alert variant="info" data-testid="topup-credited">
              <AlertTitle>Funds added</AlertTitle>
              <AlertDescription>Your balance has been credited.</AlertDescription>
            </Alert>
          ) : (
            <Alert variant="info" data-testid="topup-pending">
              <AlertTitle>{pollStopped ? 'Still waiting for Stripe' : 'Confirming your payment…'}</AlertTitle>
              <AlertDescription>
                {pollStopped
                  ? 'The credit is taking longer than usual. It is applied once Stripe confirms the payment; reload this page in a minute.'
                  : 'The balance updates as soon as Stripe confirms the payment, usually within seconds.'}
              </AlertDescription>
            </Alert>
          )
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CoinsIcon className="size-4 text-primary" />
              Balance
            </CardTitle>
            <CardDescription>
              Today, <strong>managed</strong> police is the only thing that meters against this
              balance. Delegated police — your own agent or one of your users holding the seat — is
              free and always will be.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {balance.isPending ? (
              <TableSkeleton rows={1} />
            ) : balance.isError ? (
              <ErrorState
                error={balance.error}
                title="Could not load the balance"
                onRetry={() => {
                  void balance.refetch()
                }}
              />
            ) : (
              <>
                <p className="text-3xl font-semibold tabular-nums" data-testid="police-balance">
                  {formatMicros(balance.data.balance_micros, currency)}
                </p>

                {balance.data.balance_micros <= 0 ? (
                  <Alert variant="destructive">
                    <AlertTriangleIcon className="size-4" />
                    <AlertTitle>Managed police is stopped</AlertTitle>
                    <AlertDescription>
                      Evaluations are refused while the balance is empty. Any zone set to halt when
                      police is inactive has paused its agents, and resuming them is your decision —
                      a top-up alone does not release a held backlog.
                    </AlertDescription>
                  </Alert>
                ) : null}

                {readOnly ? null : (
                  <form onSubmit={submit} className="flex flex-col gap-3" data-testid="topup-form">
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex flex-col gap-1">
                        <label htmlFor="topup" className="text-xs text-muted-foreground">
                          Add funds ({currency})
                        </label>
                        <Input
                          id="topup"
                          value={amount}
                          inputMode="decimal"
                          className="w-32"
                          aria-invalid={!check.ok}
                          onChange={(event) => {
                            setAmount(event.target.value)
                          }}
                        />
                      </div>
                      <Button type="submit" disabled={topUp.isPending || !check.ok}>
                        <PlusIcon className="size-3.5" />
                        {topUp.isPending ? 'Opening Checkout…' : 'Add funds'}
                      </Button>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={saveCard}
                        onChange={(event) => {
                          setSaveCard(event.target.checked)
                        }}
                      />
                      Save this card for auto-recharge
                    </label>
                    {/* Stated before submitting, not discovered in a 422. The server still enforces
                        it -- a form check is a convenience, never the control. */}
                    <p className="text-xs text-muted-foreground">
                      Minimum {formatUsd(minimumUsd)}, plus applicable tax, calculated at checkout.
                      {check.ok ? '' : ` ${check.reason}`} You pay on Stripe's secure page, get a tax
                      invoice, and come back here. The balance is credited with the amount before tax.
                    </p>
                  </form>
                )}

                {topUp.isError ? (
                  <Alert variant="destructive">
                    <AlertTitle>Could not start the top-up</AlertTitle>
                    <AlertDescription>{errorMessage(topUp.error)}</AlertDescription>
                  </Alert>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>

        <RefundCard />

        <AutoRecharge />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ReceiptIcon className="size-4 text-primary" />
              Transactions
            </CardTitle>
            <CardDescription>
              Every charge shows the tokens it billed for and the margin applied.{' '}
              <strong>Calls that fail or time out are still charged</strong> for the tokens they
              consumed — the model provider bills us for those, so they appear here too.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {transactions.isPending ? (
              <TableSkeleton rows={4} />
            ) : transactions.isError ? (
              <ErrorState
                error={transactions.error}
                title="Could not load the transactions"
                onRetry={() => {
                  void transactions.refetch()
                }}
              />
            ) : transactions.data.length === 0 ? (
              <EmptyState
                icon={ReceiptIcon}
                title="Nothing yet"
                description="Top-ups and metered charges will appear here."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>What</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.data.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatAbsolute(entry.created_at)}
                      </TableCell>
                      <TableCell>
                        <Description entry={entry} />
                      </TableCell>
                      <TableCell
                        className={
                          'text-right text-sm tabular-nums ' +
                          (entry.amount_micros < 0 ? 'text-muted-foreground' : 'text-foreground')
                        }
                      >
                        {entry.amount_micros > 0 ? '+' : ''}
                        {formatMicros(entry.amount_micros, entry.currency)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatMicros(entry.balance_after_micros, entry.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </PageBody>
      {checkout === null ? null : (
        <EmbeddedCheckoutDialog
          clientSecret={checkout.client_secret}
          title="Add funds"
          description="Pay securely with Stripe, right here. Your balance is credited once Stripe confirms the payment."
          onComplete={() => {
            setCheckout(null)
            void navigate(`/app/billing?topup=complete&session_id=${encodeURIComponent(checkout.session_id)}`)
          }}
          onClose={() => {
            setCheckout(null)
          }}
        />
      )}
    </>
  )
}

/**
 * One row's explanation.
 *
 * A charge lists all four counters, including both cache figures — they are priced differently, and
 * a customer comparing our number to the provider's needs to see the same breakdown the provider
 * shows.
 */
function Description({ entry }: { entry: BalanceTransactionRow }) {
  if (entry.kind !== 'CHARGE') {
    const document = ledgerDocument(entry)
    return (
      <span className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{entry.kind.toLowerCase()}</Badge>
        <span className="text-sm">{entry.description ?? ''}</span>
        {document === null ? null : (
          <PdfButton
            path={document.path}
            fallbackName={`${document.label.toLowerCase().replace(' ', '-')}.pdf`}
            label={document.label}
            testId="ledger-document"
          />
        )}
      </span>
    )
  }

  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex items-center gap-2">
        <Badge variant="outline">police</Badge>
        <span className="font-mono text-xs">{entry.model_id ?? 'model'}</span>
        {entry.markup_bps === null ? null : (
          <span className="text-xs text-muted-foreground">+{formatBps(entry.markup_bps)}</span>
        )}
      </span>
      <span className="text-xs text-muted-foreground tabular-nums">
        {entry.input_tokens ?? 0} in · {entry.output_tokens ?? 0} out
        {entry.cache_write_tokens ? ` · ${entry.cache_write_tokens} cache write` : ''}
        {entry.cache_read_tokens ? ` · ${entry.cache_read_tokens} cache read` : ''}
      </span>
    </span>
  )
}

/** Auto-recharge settings (B11): off by default; below the threshold, add the amount from a saved card. */
function AutoRecharge() {
  const settings = usePoliceRecharge()
  return (
    <Card data-testid="auto-recharge">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCwIcon className="size-4 text-primary" />
          Auto-recharge
        </CardTitle>
        <CardDescription>
          Keep managed police running: when the balance falls below a threshold, charge your saved
          card for a fixed amount. It is off until you turn it on, and needs a card saved with a
          top-up.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {settings.isPending ? (
          <TableSkeleton rows={2} />
        ) : settings.isError ? (
          <ErrorState
            error={settings.error}
            title="Could not load the auto-recharge settings"
            onRetry={() => {
              void settings.refetch()
            }}
          />
        ) : (
          <RechargeForm settings={settings.data} />
        )}
      </CardContent>
    </Card>
  )
}

function RechargeForm({ settings }: { settings: PoliceRecharge }) {
  const save = useSavePoliceRecharge()
  const readOnly = useReadOnly()
  const [enabled, setEnabled] = useState(settings.enabled)
  const [threshold, setThreshold] = useState(String(settings.threshold_usd))
  const [amount, setAmount] = useState(String(settings.amount_usd))

  const thresholdCheck = checkUsdAmount(threshold, 0.01)
  const amountCheck = checkUsdAmount(amount, settings.minimum_amount_usd)
  const valid = !enabled || (thresholdCheck.ok && amountCheck.ok)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!valid) return
    save.mutate(
      enabled && thresholdCheck.ok && amountCheck.ok
        ? { enabled, threshold_usd: thresholdCheck.value, amount_usd: amountCheck.value }
        : { enabled },
    )
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {settings.enabled && settings.active ? (
        <Alert variant="info" data-testid="recharge-active">
          <AlertDescription>
            Active: below {formatUsd(settings.threshold_usd)} we add {formatUsd(settings.amount_usd)}{' '}
            (at most {settings.max_per_day} times a day).
          </AlertDescription>
        </Alert>
      ) : null}
      {settings.enabled && !settings.active ? (
        <Alert variant="warning" data-testid="recharge-inactive">
          <AlertTitle>Saved, but not active</AlertTitle>
          <AlertDescription>
            {settings.card_saved
              ? 'Billing is currently unavailable, so the balance will not top itself up.'
              : 'No card is saved yet. Make a top-up above with "Save this card for auto-recharge" ticked.'}
          </AlertDescription>
        </Alert>
      ) : null}
      {!settings.card_saved ? (
        <p className="text-xs text-muted-foreground">
          No card is saved. Tick "Save this card for auto-recharge" on a top-up to save one.
        </p>
      ) : null}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-primary"
          checked={enabled}
          disabled={readOnly}
          data-testid="recharge-enabled"
          onChange={(event) => {
            setEnabled(event.target.checked)
          }}
        />
        Recharge automatically
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor="recharge-threshold" className="text-xs text-muted-foreground">
            When the balance falls below (USD)
          </label>
          <Input
            id="recharge-threshold"
            value={threshold}
            inputMode="decimal"
            disabled={!enabled || readOnly}
            aria-invalid={enabled && !thresholdCheck.ok}
            onChange={(event) => {
              setThreshold(event.target.value)
            }}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <label htmlFor="recharge-amount" className="text-xs text-muted-foreground">
            Add (USD, at least {formatUsd(settings.minimum_amount_usd)})
          </label>
          <Input
            id="recharge-amount"
            value={amount}
            inputMode="decimal"
            disabled={!enabled || readOnly}
            aria-invalid={enabled && !amountCheck.ok}
            onChange={(event) => {
              setAmount(event.target.value)
            }}
          />
        </div>
      </div>
      {enabled && !valid ? (
        <p className="text-xs text-destructive">
          {!thresholdCheck.ok ? `Threshold: ${thresholdCheck.reason}` : !amountCheck.ok ? amountCheck.reason : ''}
        </p>
      ) : null}
      {save.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage(save.error)}</AlertDescription>
        </Alert>
      ) : null}
      {readOnly ? null : (
        <div>
          <Button type="submit" disabled={!valid || save.isPending}>
            {save.isPending ? 'Saving…' : save.isSuccess ? 'Saved' : 'Save settings'}
          </Button>
        </div>
      )}
    </form>
  )
}

/** The server's refusal of a refund request, in words; the cap is quoted back when it moved. */
function refundErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'refund_exceeds_refundable': {
        const micros = error.problem?.refundable_micros
        return typeof micros === 'number'
          ? `That is more than is refundable now (${formatMicros(micros)}). Reload and try a smaller amount.`
          : 'That is more than is refundable now.'
      }
      case 'invalid_amount':
        return 'Enter a dollar amount with at most two decimals.'
      case 'reason_required':
        return 'Tell us why you are asking for a refund.'
      default:
        return errorMessage(error)
    }
  }
  return errorMessage(error)
}

/**
 * "Refundable now" and the request flow (T4-T6). Refundable is what paid top-ups still hold
 * unspent: every police call already made is charged and non-refundable, and goodwill credit is
 * never cash. The admin only REQUESTS; a staff superoperator approves or rejects, and the amount is
 * held aside meanwhile.
 */
function RefundCard() {
  const refundable = useRefundable()
  const readOnly = useReadOnly()
  const [open, setOpen] = useState(false)
  return (
    <Card data-testid="refund-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UndoIcon className="size-4 text-primary" />
          Refunds
        </CardTitle>
        <CardDescription>
          Unspent top-ups can be refunded to your card with their tax. Police calls already made are
          charged and are not refundable, and neither is credit we gave you. A request is reviewed by
          our team.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {refundable.isPending ? (
          <TableSkeleton rows={1} />
        ) : refundable.isError ? (
          <ErrorState
            error={refundable.error}
            title="Could not load what is refundable"
            onRetry={() => {
              void refundable.refetch()
            }}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Refundable now</p>
                <p className="text-2xl font-semibold tabular-nums" data-testid="refundable-now">
                  {formatMicros(refundable.data.refundable_micros)}
                </p>
                {refundable.data.reserved_by_requests_micros > 0 ? (
                  <p className="text-xs text-muted-foreground tabular-nums" data-testid="refund-reserved">
                    {formatMicros(refundable.data.reserved_by_requests_micros)} is held aside by pending requests.
                  </p>
                ) : null}
              </div>
              {readOnly ? null : (
                <Button
                  variant="outline"
                  disabled={refundable.data.refundable_micros <= 0}
                  onClick={() => {
                    setOpen(true)
                  }}
                >
                  Request a refund
                </Button>
              )}
            </div>
            <RefundRequests data={refundable.data} />
            {open ? (
              <RefundDialog
                refundableMicros={refundable.data.refundable_micros}
                onClose={() => {
                  setOpen(false)
                }}
              />
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function RefundRequests({ data }: { data: Refundable }) {
  if (data.requests.length === 0) return null
  return (
    <ul className="flex flex-col gap-2" data-testid="refund-requests">
      {data.requests.map((request) => (
        <li
          key={request.id}
          className="flex flex-col gap-0.5 rounded-md border p-3 text-sm"
          data-testid="refund-request"
          data-state={request.state}
        >
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium tabular-nums">{formatMicros(request.amount_micros)}</span>
            <Badge variant={refundRequestOpen(request.state) ? 'default' : 'secondary'}>
              {refundRequestLabel(request.state)}
            </Badge>
            <span className="text-xs text-muted-foreground">{formatAbsolute(request.created_at)}</span>
          </span>
          {request.reason === null ? null : (
            <span className="text-xs text-muted-foreground">You wrote: {request.reason}</span>
          )}
          {request.decision_reason === null ? null : (
            <span className="text-xs text-muted-foreground">Our answer: {request.decision_reason}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

function RefundDialog({ refundableMicros, onClose }: { refundableMicros: number; onClose: () => void }) {
  const request = useRequestRefund()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const check = checkRefundAmount(amount, refundableMicros)
  const ready = check.ok && reason.trim().length > 0 && !request.isPending

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!check.ok || !ready) return
    request.mutate({ amountUsd: check.value, reason: reason.trim() }, { onSuccess: onClose })
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !request.isPending) onClose()
      }}
    >
      <DialogContent data-testid="refund-dialog">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Request a refund</DialogTitle>
            <DialogDescription>
              Up to {formatMicros(refundableMicros)} can be refunded now. The amount is held aside while
              our team reviews it, and goes back to your card together with its tax.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1">
            <label htmlFor="refund-amount" className="text-xs text-muted-foreground">
              Amount (USD)
            </label>
            <Input
              id="refund-amount"
              value={amount}
              inputMode="decimal"
              aria-invalid={amount !== '' && !check.ok}
              onChange={(event) => {
                setAmount(event.target.value)
              }}
            />
            {amount !== '' && !check.ok ? (
              <p className="text-xs text-destructive">{check.reason}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="refund-reason" className="text-xs text-muted-foreground">
              Why are you asking?
            </label>
            <Textarea
              id="refund-reason"
              value={reason}
              maxLength={2000}
              onChange={(event) => {
                setReason(event.target.value)
              }}
            />
          </div>
          {request.isError ? (
            <Alert variant="destructive" data-testid="refund-error">
              <AlertDescription>{refundErrorText(request.error)}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={request.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready}>
              {request.isPending ? 'Sending…' : 'Send request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
